/**
 * useTourManager
 * Central tour management hook
 */

import { TOUR_DATE_PREFIX, TOUR_STORAGE_PREFIX, TourManager, TourName } from '@shared/lib/tours/types'
import { useCallback } from 'react'

export function useTourManager(): TourManager {
  /**
   * Check if a tour has been completed
   */
  const isTourCompleted = useCallback((tourName: TourName): boolean => {
    const key = `${TOUR_STORAGE_PREFIX}${tourName}`
    return localStorage.getItem(key) === 'true'
  }, [])

  /**
   * Check if a tour should be shown
   * Returns true if tour hasn't been completed
   */
  const shouldShowTour = useCallback(
    (tourName: TourName): boolean => {
      return !isTourCompleted(tourName)
    },
    [isTourCompleted]
  )

  /**
   * Start a tour (to be implemented by individual tour hooks)
   * This is a placeholder - actual tour start is handled by individual hooks
   */
  const startTour = useCallback((): void => {
    // Individual tour hooks will handle the actual driver.js initialization
  }, [])

  /**
   * Reset a specific tour (mark as not completed)
   */
  const resetTour = useCallback((tourName: TourName): void => {
    const key = `${TOUR_STORAGE_PREFIX}${tourName}`
    const dateKey = `${TOUR_DATE_PREFIX}${tourName}`

    localStorage.removeItem(key)
    localStorage.removeItem(dateKey)
  }, [])

  /**
   * Reset all tours
   */
  const resetAllTours = useCallback((): void => {
    const tours: TourName[] = ['home', 'professionals-list', 'professional-detail', 'profile', 'professions', 'professional-profile-form']

    tours.forEach((tourName) => {
      resetTour(tourName)
    })
  }, [resetTour])

  /**
   * Get the completion date of a tour
   */
  const getTourCompletionDate = useCallback((tourName: TourName): string | null => {
    const dateKey = `${TOUR_DATE_PREFIX}${tourName}`
    return localStorage.getItem(dateKey)
  }, [])

  return {
    startTour,
    resetTour,
    resetAllTours,
    isTourCompleted,
    shouldShowTour,
    getTourCompletionDate,
  }

}

/**
 * Helper function to mark tour as completed
 * Can be used outside of React components
 */
export function markTourAsCompleted(tourName: TourName): void {
  const key = `${TOUR_STORAGE_PREFIX}${tourName}`
  const dateKey = `${TOUR_DATE_PREFIX}${tourName}`
  const now = new Date().toISOString()

  localStorage.setItem(key, 'true')
  localStorage.setItem(dateKey, now)
}
